/**
 * Recent Activity table + Financial Metrics Matrix + Detailed Drill-Down Table.
 */
import React from 'react';
import { Minimize2, Maximize2, Table2, Download, Filter } from 'lucide-react';
import { StatusBadge, getHeatColor } from './analyticsHelpers.jsx';

export default function RecentActivityMatrix({ 
  analytics, recent_activity, matrixData, 
  expandedSections, toggleSection,
  handleDrillDown, exportMatrixCSV,
  showDetailedTable, filteredDetailedData
}) {
  if (!analytics) return null;

  return (
    <>
      {/* Recent Activity */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Recent Activity</h3>
          <button onClick={() => toggleSection('detailed')} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            {expandedSections.detailed ? 'Hide Details' : 'Show All Details'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Scenario</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Service</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Creator</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Created</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {recent_activity.map((scenario) => (
                <tr key={scenario.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 text-sm text-slate-900">{scenario.name}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{scenario.service_name || 'N/A'}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{scenario.creator_name || 'Unknown'}</td>
                  <td className="py-3 px-4"><StatusBadge status={scenario.status} /></td>
                  <td className="py-3 px-4 text-sm text-slate-600">{new Date(scenario.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-green-600">
                    ${((scenario.total_net_revenue || scenario.net_revenue || 0) / 1000).toFixed(1)}K
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {recent_activity.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No recent activity</p>}
        </div>
      </div>

      {/* Financial Metrics Matrix */}
      {matrixData && (
        <div className="glass-card p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button onClick={() => toggleSection('matrix')} className="text-slate-400 hover:text-slate-600">
                {expandedSections.matrix ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
              <Table2 className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-slate-900">Financial Metrics Matrix</h3>
              <span className="text-sm text-slate-500">({matrixData.matrix_data.length} services)</span>
            </div>
            <button onClick={exportMatrixCSV} className="btn-secondary inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> Export
            </button>
          </div>
          
          {expandedSections.matrix && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-200 bg-slate-50">
                    {['Service','Scenarios','Gross (Sum)','Gross (Avg)','Net (Sum)','Net (Avg)','Payments (Sum)','Payments (Avg)','Take Rate %','Approval %'].map((h,i) => (
                      <th key={i} className={`py-3 px-4 text-sm font-semibold text-slate-700 ${i <= 0 ? 'text-left' : i === 1 ? 'text-center' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixData.matrix_data.map((row, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => handleDrillDown('service', row.service_name)}>
                      <td className="py-3 px-4 text-sm font-medium text-slate-900">{row.service_name}</td>
                      <td className="py-3 px-4 text-sm text-center text-slate-700">{row.scenario_count}</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900">${(row.gross_revenue_sum / 1000).toFixed(1)}K</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-700">${(row.gross_revenue_avg / 1000).toFixed(1)}K</td>
                      <td className="py-3 px-4 text-sm text-right font-semibold text-green-600">${(row.net_revenue_sum / 1000).toFixed(1)}K</td>
                      <td className="py-3 px-4 text-sm text-right text-green-600">${(row.net_revenue_avg / 1000).toFixed(1)}K</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900">${(row.total_payments_sum / 1000).toFixed(1)}K</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-700">${(row.total_payments_avg / 1000).toFixed(1)}K</td>
                      <td className="py-3 px-4 text-sm text-right font-semibold text-indigo-600">{row.implied_take_rate_avg}%</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-700">{row.approval_rate}%</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-indigo-50 font-semibold">
                    <td className="py-3 px-4 text-sm text-slate-900">{matrixData.totals.service_name}</td>
                    <td className="py-3 px-4 text-sm text-center text-slate-900">{matrixData.totals.scenario_count}</td>
                    <td className="py-3 px-4 text-sm text-right text-slate-900">${(matrixData.totals.gross_revenue_sum / 1000).toFixed(1)}K</td>
                    <td className="py-3 px-4 text-sm text-right text-slate-900">${(matrixData.totals.gross_revenue_avg / 1000).toFixed(1)}K</td>
                    <td className="py-3 px-4 text-sm text-right text-green-700">${(matrixData.totals.net_revenue_sum / 1000).toFixed(1)}K</td>
                    <td className="py-3 px-4 text-sm text-right text-green-700">${(matrixData.totals.net_revenue_avg / 1000).toFixed(1)}K</td>
                    <td className="py-3 px-4 text-sm text-right text-slate-900">${(matrixData.totals.total_payments_sum / 1000).toFixed(1)}K</td>
                    <td className="py-3 px-4 text-sm text-right text-slate-900">${(matrixData.totals.total_payments_avg / 1000).toFixed(1)}K</td>
                    <td className="py-3 px-4 text-sm text-right text-indigo-700">{matrixData.totals.implied_take_rate_avg}%</td>
                    <td className="py-3 px-4 text-sm text-right text-slate-900">{matrixData.totals.approval_rate}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Detailed Drill-Down Table */}
      {showDetailedTable && analytics.detailed_scenarios && (
        <div className="glass-card p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              <Filter className="w-5 h-5 inline mr-2 text-indigo-600" />
              Detailed Scenarios ({filteredDetailedData.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-slate-50">
                  {['Name','Service','Creator','Status','Created','Gross Revenue','Net Revenue'].map((h,i) => (
                    <th key={i} className={`py-2 px-3 font-semibold text-slate-700 ${i >= 5 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDetailedData.slice(0, 50).map((s, idx) => {
                  const maxGross = Math.max(...filteredDetailedData.map(d => d.gross_revenue || d.total_gross_revenue || 0));
                  return (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-900 font-medium">{s.name}</td>
                      <td className="py-2 px-3 text-slate-600">{s.service_name}</td>
                      <td className="py-2 px-3 text-slate-600">{s.creator_name}</td>
                      <td className="py-2 px-3"><StatusBadge status={s.status} /></td>
                      <td className="py-2 px-3 text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '-'}</td>
                      <td className="py-2 px-3 text-right font-medium" style={{ backgroundColor: getHeatColor(s.gross_revenue || s.total_gross_revenue || 0, maxGross) }}>
                        ${((s.gross_revenue || s.total_gross_revenue || 0) / 1000).toFixed(1)}K
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-green-700">
                        ${((s.net_revenue || s.total_net_revenue || 0) / 1000).toFixed(1)}K
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredDetailedData.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No scenarios match the current drill-down criteria</p>}
          </div>
        </div>
      )}
    </>
  );
}
